import { action, makeObservable } from 'mobx';

import _, { get, set } from 'lodash';

import { ProtectorTypesWithoutProperties } from 'utils/api/types';
import { ProtectorType, RepeatingGroupVariant } from 'core/constants';
import { EntityFieldRoles, EnumOptions, ImageType } from 'utils/api/modules/entities/entities.types';

import API from 'utils/api';

import FormBuilder from '../FormBuilder';

class QuestionLogicStore {
  constructor(private formBuilder: FormBuilder) {
    makeObservable(this);
  }

  @action.bound
  async updatedQuestionValues(
    id: string,
    questionType?: ProtectorType,
    title?: string,
    lookupType?: string,
    properties?: { enum?: EnumOptions; variant?: RepeatingGroupVariant }
  ): Promise<void> {
    if (!this.formBuilder.data) return;
    const sectionId = this.formBuilder.dragAndDropManager.findItem(id);
    if (!sectionId) return;

    const questionPath = `properties.${sectionId}.properties.${id}`;

    this.formBuilder.data = set({ ...this.formBuilder.data }, `${questionPath}.description`, title);
    this.formBuilder.data = set({ ...this.formBuilder.data }, `${questionPath}.questionType`, questionType);

    if (questionType === ProtectorType.MultiChoice || questionType === ProtectorType.SingleChoice) {
      // get old properties for this question and check which options was removed
      const oldOptions = get(this.formBuilder.data, `${questionPath}.properties.enum`, []) as EnumOptions;
      const newOptions = properties?.enum || [];
      const removedOptions = oldOptions.filter((opt) => !newOptions.some((newOpt) => newOpt.key === opt.key));

      removedOptions.forEach((opt) => {
        this.formBuilder.formAction.removeActionsWithOptionKey(sectionId, opt.key);
      });

      // in these properties, we have the image property, and we do not want to remove it
      const { image } = get(this.formBuilder.data, `${questionPath}.properties`, {}) as { image?: File };
      const mergedProps = { ...properties, image };

      this.formBuilder.data = set({ ...this.formBuilder.data }, `${questionPath}.properties`, mergedProps);
    }
    // if the field is a lookupType field, set the lookupType field
    if (questionType === ProtectorType.Lookup) {
      this.formBuilder.data = set({ ...this.formBuilder.data }, `${questionPath}.properties.lookupType`, lookupType);
    }

    // if the field type is not multi-choice or single choice or lookup, set the properties to an empty object
    if (ProtectorTypesWithoutProperties.includes(questionType as ProtectorType)) {
      this.formBuilder.data = set({ ...this.formBuilder.data }, `${questionPath}.properties.enum`, undefined);
      this.formBuilder.data = set({ ...this.formBuilder.data }, `${questionPath}.properties.lookupType`, undefined);
    }

    if (questionType === ProtectorType.RepeatingGroup) {
      this.formBuilder.data = set(
        { ...this.formBuilder.data },
        `${questionPath}.properties.variant`,
        properties?.variant
      );
    }

    // upload image to backend and save the image uuid
    await this.uploadImage(sectionId, id);

    this.formBuilder.setActiveUploadImage(undefined);
  }

  async uploadImage(sectionId: string, id: string): Promise<void> {
    if (!this.formBuilder.data) return;

    // upload image to backend and save the image uuid
    const image = this.getImageFromQuestion(sectionId, id);
    // If the image is a type of file, upload it to the backend
    // if the image is a string, it means the image is already uploaded
    if (image && typeof image !== 'string') {
      try {
        const response = await API.entities.uploadEntityFormImage('events', image)();

        this.formBuilder.data = set(
          { ...this.formBuilder.data },
          `properties.${sectionId}.properties.${id}.properties.image`,
          { uuid: response.data.uuid, file: image }
        );
      } catch (error) {
        this.formBuilder.store.notification.enqueueErrorSnackbar('Error uploading image');
      }
    }
  }

  // delete question
  deleteQuestion = (sectionId: string, fieldId: string): void => {
    if (!this.formBuilder.data) return;
    this.formBuilder.data = set(
      { ...this.formBuilder.data },
      `properties.${sectionId}.order`,
      this.formBuilder.items[sectionId].filter((field) => field !== fieldId)
    );
    delete this.formBuilder.data.properties[sectionId].properties[fieldId];
    this.formBuilder.items = {
      ...this.formBuilder.items,
      [sectionId]: this.formBuilder.items[sectionId].filter((field) => field !== fieldId),
    };

    // remove the field from the required fields
    this.formBuilder.data = set(
      { ...this.formBuilder.data },
      `properties.${sectionId}.required`,
      this.formBuilder.data.properties[sectionId].required.filter((field) => field !== fieldId)
    );

    // remove the field from the actions
    this.formBuilder.formAction.removeQuestionActions(sectionId, fieldId);
  };

  // duplicate question
  duplicateQuestion = (sectionId: string, fieldId: string): void => {
    if (!this.formBuilder.data) return;
    const newFieldId = this.formBuilder.getNextContainerId();
    const newField = _.cloneDeep(this.formBuilder.data?.properties[sectionId].properties[fieldId]);

    const fieldIndex = this.formBuilder.data.properties[sectionId].order.indexOf(fieldId);

    // updating order
    this.formBuilder.data = set({ ...this.formBuilder.data }, `properties.${sectionId}.order`, [
      ...this.formBuilder.data.properties[sectionId].order.slice(0, fieldIndex + 1),
      newFieldId,
      ...this.formBuilder.data.properties[sectionId].order.slice(fieldIndex + 1),
    ]);

    // updating properties
    this.formBuilder.data = set(
      { ...this.formBuilder.data },
      `properties.${sectionId}.properties.${newFieldId}`,
      newField
    );

    this.formBuilder.items = {
      ...this.formBuilder.items,
      [sectionId]: [
        ...this.formBuilder.items[sectionId].slice(0, fieldIndex + 1),
        newFieldId,
        ...this.formBuilder.items[sectionId].slice(fieldIndex + 1),
      ],
    };

    // if duplicated field was required, make the new field required
    if (this.formBuilder.data.properties[sectionId].required.includes(fieldId)) {
      this.makeQuestionRequired(sectionId, newFieldId);
    }

    // if duplicated field had actions, duplicate the actions
    this.formBuilder.formAction.duplicateQuestionActions(sectionId, fieldId, newFieldId);
  };

  // make question required
  makeQuestionRequired = (sectionId: string, fieldId: string): void => {
    if (!this.formBuilder.data) return;
    // if field was already required, remove it from required fields
    if (this.formBuilder.data.properties[sectionId].required.includes(fieldId)) {
      this.formBuilder.data = set(
        { ...this.formBuilder.data },
        `properties.${sectionId}.required`,
        this.formBuilder.data.properties[sectionId].required.filter((field) => field !== fieldId)
      );
    } else {
      // otherwise add it to required fields
      this.formBuilder.data = set({ ...this.formBuilder.data }, `properties.${sectionId}.required`, [
        ...this.formBuilder.data.properties[sectionId].required,
        fieldId,
      ]);
    }
  };

  isQuestionRequired = (sectionId: string, fieldId: string): boolean => {
    if (!this.formBuilder.data) return false;
    return this.formBuilder.data.properties[sectionId].required.includes(fieldId);
  };

  toggleNoFutureDate = (sectionId: string, fieldId: string): void => {
    if (!this.formBuilder.data) return;

    const rolesPath = `properties.${sectionId}.properties.${fieldId}.properties.roles`;
    const currentRoles = (get(this.formBuilder.data, rolesPath, []) as EntityFieldRoles[]) || [];

    const nextRoles = currentRoles.includes(EntityFieldRoles.NO_FUTURE_DATE)
      ? currentRoles.filter((role) => role !== EntityFieldRoles.NO_FUTURE_DATE)
      : [...currentRoles, EntityFieldRoles.NO_FUTURE_DATE];

    this.formBuilder.data = set({ ...this.formBuilder.data }, rolesPath, nextRoles.length ? nextRoles : undefined);
  };

  isNoFutureDateEnabled = (sectionId: string, fieldId: string): boolean => {
    if (!this.formBuilder.data) return false;
    const roles =
      (get(
        this.formBuilder.data,
        `properties.${sectionId}.properties.${fieldId}.properties.roles`,
        []
      ) as EntityFieldRoles[]) || [];
    return roles.includes(EntityFieldRoles.NO_FUTURE_DATE);
  };

  addImageToQuestion = (sectionId: string, fieldId: string, image: File | undefined): void => {
    if (!this.formBuilder.data) return;

    if (image === undefined) {
      this.formBuilder.data = set(
        { ...this.formBuilder.data },
        `properties.${sectionId}.properties.${fieldId}.properties.image`,
        undefined
      );
      return;
    }

    const imageObject = {
      file: image,
    };

    this.formBuilder.data = set(
      { ...this.formBuilder.data },
      `properties.${sectionId}.properties.${fieldId}.properties.image`,
      imageObject
    );
  };

  getImageFromQuestion = (sectionId: string, fieldId: string): File | string | undefined => {
    if (!this.formBuilder.data) return undefined;
    const field = this.formBuilder.data.properties[sectionId].properties[fieldId];

    if (field.properties?.image) {
      return (field.properties.image as unknown as ImageType).file;
    }

    return undefined;
  };
}

export default QuestionLogicStore;
